import { useEffect, useRef } from 'react';

/**
 * Focus trap hook for modal accessibility
 * Traps keyboard focus within a modal and handles ESC key
 */
export function useFocusTrap(
  isOpen: boolean,
  onClose: () => void,
  options: {
    returnFocusOnClose?: boolean;
    initialFocusRef?: React.RefObject<HTMLElement>;
  } = {}
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const { returnFocusOnClose = true, initialFocusRef } = options;

  useEffect(() => {
    if (!isOpen) return;

    // Store the element that had focus before modal opened
    previousActiveElement.current = document.activeElement as HTMLElement;

    // Set initial focus
    const setInitialFocus = () => {
      if (initialFocusRef?.current) {
        initialFocusRef.current.focus();
      } else {
        // Focus first focusable element
        const focusableElements = getFocusableElements();
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        }
      }
    };

    // Small delay to ensure modal is rendered
    const timeout = setTimeout(setInitialFocus, 10);

    return () => clearTimeout(timeout);
  }, [isOpen, initialFocusRef]);

  useEffect(() => {
    if (!isOpen) {
      // Return focus to previous element
      if (returnFocusOnClose && previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      // Close modal on ESC
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }

      // Tab key handling for focus trap
      if (e.key === 'Tab') {
        const focusableElements = getFocusableElements();
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          // Shift+Tab: going backwards
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab: going forwards
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, returnFocusOnClose]);

  const getFocusableElements = (): HTMLElement[] => {
    if (!containerRef.current) return [];

    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable]',
    ].join(', ');

    return Array.from(containerRef.current.querySelectorAll(selector));
  };

  return containerRef;
}
